import { useHotkeys } from "react-hotkeys-hook";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dialog } from "radix-ui";
import { Link, useHistory } from "react-router-dom";
import { clsx } from "clsx";

import { Input } from "../Input/Input";
import { useNavItems } from "@/NavBar/NavBar";
import { NavItemProps, RenderNavItemsProps } from "@/NavBar/types";

export function Commandbar() {
  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  useHotkeys("meta+k,ctrl+k", () => setOpen((o) => !o));

  const handleQueryInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setQuery(e.target.value);
      setSelectedIndex(0);
    },
    [],
  );

  const close = useCallback(() => {
    setOpen(false);
  }, []);
  const [query, setQuery] = useState("");
  const commands = useSearchableCommands();

  const filteredCommands = useMemo(() => {
    const safeSearch = query.trim().toLowerCase();
    return commands.filter((command) =>
      command.searchKeys.some((key) => key.includes(safeSearch)),
    );
  }, [commands, query]);

  const navigate = useHistory().push;
  const handleInputKeydown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex(Math.max(selectedIndex - 1, 0));
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex(
          Math.min(selectedIndex + 1, filteredCommands.length - 1),
        );
      }
      if (e.key === "Enter") {
        const command = filteredCommands[selectedIndex];
        if (command) {
          const command = filteredCommands[selectedIndex];
          if (!command) return;
          setOpen(false);
          navigate(command.href);
        }
      }
    },
    [selectedIndex, filteredCommands, navigate],
  );
  const wrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const selectedItem = wrapperRef.current?.querySelector(
      `[data-index="${selectedIndex}"]`,
    );
    if (selectedItem instanceof HTMLElement) {
      selectedItem.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [selectedIndex]);

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Title className="sr-only">Command bar</Dialog.Title>
        <Dialog.Description className="sr-only">
          Search for routes in the application
        </Dialog.Description>
        <Dialog.Content
          onEscapeKeyDown={close}
          onPointerDownOutside={close}
          className="border-dividerBold data-[state=open]:animate-fadeIn data-[state=closed]:animate-fadeOut fixed top-1/6 left-1/2 z-10 w-125 -translate-x-1/2 space-y-4 rounded-md border bg-gray-50/90 p-4 shadow-md"
        >
          <Input
            value={query}
            onChange={handleQueryInputChange}
            label="Search commands"
            onKeyDown={handleInputKeydown}
          />

          <div className="max-h-96 overflow-auto rounded-md" ref={wrapperRef}>
            {filteredCommands.map((command, i) => (
              <Link
                to={command.href}
                key={command.title}
                data-index={`${i}`}
                className={clsx(
                  "flex items-center gap-4 rounded-md px-4 py-2 hover:bg-gray-100/90",
                  i === selectedIndex && "bg-gray-200/70",
                )}
              >
                <span>{command.title}</span>
                <span className="text-textSubtle text-xs">{command.label}</span>
              </Link>
            ))}
            {!filteredCommands.length && (
              <div className="text-textSubtle flex items-center justify-center rounded-md px-4 py-2">
                No commands found
              </div>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

interface Command {
  title: string;
  label: string;
  href: string;
  searchKeys: string[];
}
function useSearchableCommands() {
  const navItems = useNavItems();

  return useMemo<Command[]>(() => {
    const full = Object.values(navItems).flat();

    return reduceNavItemsToCommands({ items: full });
  }, [navItems]);
}
function navItemToCommand(navItem: NavItemProps, prefix: string): Command {
  return {
    title: [prefix, navItem.title].filter((i) => i).join(" > "),
    label: navItem.label,
    href: navItem.href!,
    searchKeys: navItem.title
      .toLowerCase()
      .split(" ")
      .concat(prefix.toLowerCase().split(" ")),
  };
}

function reduceNavItemsToCommands(
  navItem: RenderNavItemsProps,
  prefix: string = "",
): Command[] {
  const commands: Command[] = [];
  navItem.items?.forEach((item) => {
    if (item.items) {
      commands.push(
        ...reduceNavItemsToCommands(
          item,
          [prefix, item.title].filter((i) => i).join(" > "),
        ),
      );
    } else if (item.href && (!item.shouldShowFn || item.shouldShowFn())) {
      commands.push(navItemToCommand(item, prefix));
    }
  });
  return commands;
}
